import { parseUTCToLocal } from './dateUtils';

/**
 * A work trial reaches this app the same way whether a company opened it from a
 * job posting or a test cycle created it (plan §A.8: `work-trials.source`).
 * Everything that differs between the two is framing — what the trial is called
 * and who the copy says is behind it — so it is collected here and the page
 * renders one component tree from it.
 */

export const isCycleTrial = (workTrial) => workTrial?.source === 'cycle';

/*
 * The workspace renders one component tree for both sources and varies only the
 * words in it, so its copy is collected here beside the legacy page's rather than
 * branching inside a screen. Five families, each named where it is read:
 *
 *   memoSender     — who an announcement or policy memo is from when the event
 *                    itself carries no sender name;
 *   hrDisplayName  — what the HR direct message is called before anyone has
 *                    configured a display name for it;
 *   screenEmpty    — one line per rail page, keyed by the page keys in
 *                    `components/workspace/workspacePages.js`, said in the voice
 *                    of whoever is on the other end of this trial;
 *   offboarding    — the last day, the form that closes it, and the line the day
 *                    signs off with. None of these four keys names an outcome, and
 *                    none of them may: the workspace states what happens next, and
 *                    what it never states is how the work went;
 *   videoSession   — the way out of a recording session, which is a page outside
 *                    the workspace shell and so has to be told where back is;
 *   eyebrow /      — what the stage head calls this engagement, and what it calls
 *   fallbackTitle    the trial when the trial carries no title of its own. These
 *                    two shadow same-named keys above, and they exist *because*
 *                    they do: the legacy page's words for them are "Test cycle"
 *                    and "Work Trial", both of which are vocabulary the workspace
 *                    is forbidden (plan §5.21), and the legacy page's copy is not
 *                    ours to change. So the workspace reads its own pair and the
 *                    legacy page goes on reading the pair it always read.
 *
 * Everything above this block is what the legacy page reads (`WorkTrial.js:4063`
 * reads `framing.interviewsEmpty`); nothing in it moves or changes value.
 */

/* A job trial belongs to a company that is hiring, and says so throughout. */
const JOB_FRAMING = {
    source: 'job',
    eyebrow: '',
    fallbackTitle: 'Work Trial',
    chatIntro:
        'This is your workspace for the whole trial. Start by introducing yourself, or ask anything about the process — type @HR to reach the hiring team directly.',
    projectsEmpty:
        "Projects appear here as soon as your hiring team assigns them. You'll also hear about it in Chat.",
    groupProjectsEmpty:
        'Group projects appear here as soon as your hiring team assigns them to your group.',
    interviewsEmpty: 'Interviews appear here when your hiring team schedules one for you.',
    workspace: {
        /* The company hiring is what heads a job trial, so the slot is filled from
           the trial itself and this stays empty rather than saying it twice. */
        eyebrow: '',
        fallbackTitle: 'Your workspace',
        memoSender: 'Your hiring team',
        hrDisplayName: 'Hiring team',
        screenEmpty: {
            home: 'Nothing has happened yet. Briefs, memos and schedule changes land here as your hiring team sends them.',
            chat: 'No messages yet. Introduce yourself, or ask the hiring team anything about the work.',
            tasks: 'No tasks yet. They appear here as soon as your hiring team assigns them.',
            docs: 'No documents yet. Briefs, files and revisions appear here as your hiring team sends them.',
            handbook: 'Nothing to sit in on yet. A colleague\'s walkthrough appears here when your hiring team opens the step it belongs to.',
            'work-log': 'Nothing logged yet. Write what you did today, what is next, and anything in your way.',
            video: 'No video submissions yet. They appear here when your hiring team schedules one for you.',
            announcements: 'No announcements yet. Anything your hiring team sends everyone shows up here.',
            team: 'Nobody else yet. The people you are working with appear here once your group is set.',
        },
        offboarding: {
            heading: 'Your last day',
            body: 'Your work is with the hiring team now. Access to the workspace closes at the deadline; what you submitted stays exactly as you left it.',
            feedbackPrompt: 'How was this to work in? What you say goes to the hiring team as feedback on the work, not on you.',
            closing: 'Nothing further is needed from you. The hiring team will be in touch.',
        },
        videoSession: {
            backText: 'Back to your workspace',
            successButtonText: 'Back to your workspace',
            successMessage: 'That is recorded and with your hiring team. Nothing else is needed here.',
        },
    },
};

/* A cycle trial has no company behind it: it is a scheduled run of a structure,
   released on the cycle's own timetable, with the Appli team on the other end. */
const CYCLE_FRAMING = {
    source: 'cycle',
    eyebrow: 'Test cycle',
    fallbackTitle: 'Test cycle',
    chatIntro:
        'This is your workspace for the whole test. Start by introducing yourself, or ask anything about the process — type @HR to reach the Appli team directly.',
    projectsEmpty:
        "Projects appear here as the cycle releases them. You'll also hear about it in Chat.",
    groupProjectsEmpty: 'Group projects appear here as the cycle releases them to your group.',
    interviewsEmpty: 'Interviews appear here when the cycle schedules one for you.',
    workspace: {
        /* A cycle trial has no company above it. It used to name the engagement a
           "Work placement", which is a job — and the two cohorts this was written
           for are high-school students taking a certificate (finding B19). Until
           the owner says what the word is, the slot names the thing a student is
           looking at rather than a kind of employment. */
        eyebrow: 'Your workspace',
        fallbackTitle: 'Your workspace',
        memoSender: 'The Appli team',
        hrDisplayName: 'Appli team',
        screenEmpty: {
            home: 'Nothing has happened yet. Briefs, memos and schedule changes land here as the cycle releases them.',
            chat: 'No messages yet. Introduce yourself, or ask the Appli team anything about the process.',
            tasks: 'No tasks yet. They appear here as the cycle releases them.',
            docs: 'No documents yet. Briefs, files and revisions appear here as the cycle releases them.',
            handbook: 'Nothing to sit in on yet. A colleague\'s walkthrough appears here when the cycle opens the step it belongs to.',
            'work-log': 'Nothing logged yet. Write what you did today, what is next, and anything in your way.',
            announcements: 'No announcements yet. Anything the cycle sends everyone on it shows up here.',
            team: 'Nobody else yet. The people on your group appear here once the cycle has grouped you.',
        },
        offboarding: {
            heading: 'Your last day',
            body: 'The cycle closes at the deadline and the workspace closes with it. What you submitted stays exactly as you left it.',
            feedbackPrompt: 'How was this to work in? What you say goes to the Appli team as feedback on the cycle, not on you.',
            closing: 'Nothing further is needed from you. Your certificate follows once the cycle closes.',
        },
        videoSession: {
            backText: 'Back to your workspace',
            successButtonText: 'Back to your workspace',
            successMessage: 'That is recorded and with the Appli team. Nothing else is needed here.',
        },
    },
};

/**
 * @param {Object} workTrial - Work trial as the backend returns it
 * @returns {Object} - The naming and copy this trial is rendered with
 */
export const getTrialFraming = (workTrial) =>
    isCycleTrial(workTrial) ? CYCLE_FRAMING : JOB_FRAMING;

/**
 * How far a timed trial has run, as a fraction of its window. Used to draw the
 * cycle timeline; a trial missing either end of its window cannot be placed.
 * @param {Object} workTrial - Work trial carrying started_at and ends_at
 * @param {number} now - Current epoch milliseconds
 * @returns {number|null} - 0–1, or null when the window is unknown
 */
export const getTrialProgress = (workTrial, now) => {
    const startedAt = parseUTCToLocal(workTrial?.started_at)?.getTime();
    const endsAt = parseUTCToLocal(workTrial?.ends_at)?.getTime();

    if (!startedAt || !endsAt || endsAt <= startedAt) return null;

    return Math.min(Math.max((now - startedAt) / (endsAt - startedAt), 0), 1);
};
